import {
  armHidWedge,
  disarmHidWedge,
  subscribeTagReads,
} from '../hidWedgeScanner'
import {
  listKnownScanners,
  refreshAvailableScanners,
} from '../scannerDevices'
import type {
  ConnectionEvent,
  ErrorEvent,
  KnownDevice,
  ScannerAdapter,
  ScannerStatus,
  ScannerType,
} from './types'

/**
 * Desktop / browser path: HID keyboard wedge + machine-code registry.
 */
export class HidWedgeAdapter implements ScannerAdapter {
  readonly kind = 'hid' as const

  private connectionHandlers = new Set<(ev: ConnectionEvent) => void>()
  private errorHandlers = new Set<(ev: ErrorEvent) => void>()
  private pendingType: ScannerType | null = null
  private pendingName: string | null = null

  isAvailable(): boolean {
    return typeof window !== 'undefined'
  }

  async listDevices(type: ScannerType): Promise<KnownDevice[]> {
    const refreshed = await refreshAvailableScanners(type)
    const list = refreshed.length ? refreshed : listKnownScanners(type)
    return list.map((d) => ({
      id: d.id,
      name: d.name,
      type: d.type,
      source: 'hid' as const,
    }))
  }

  async connect(type: ScannerType, name?: string, _address?: string): Promise<void> {
    const deviceName = name?.trim() || type
    armHidWedge()
    this.pendingType = type
    this.pendingName = deviceName
    // HID does not get a real SDK connection event — pending until first tag.
  }

  async disconnect(): Promise<void> {
    disarmHidWedge()
    const name = this.pendingName || ''
    const type = this.pendingType || ''
    this.pendingType = null
    this.pendingName = null
    this.connectionHandlers.forEach((h) =>
      h({ rfid_device: name, type, connected: false })
    )
  }

  async getStatus(): Promise<ScannerStatus | null> {
    return {
      connected: false,
      rfid_device: this.pendingName || '',
      type: this.pendingType || '',
    }
  }

  subscribeTags(handler: (tagId: string) => void): () => void {
    return subscribeTagReads(handler)
  }

  subscribeConnection(handler: (ev: ConnectionEvent) => void): () => void {
    this.connectionHandlers.add(handler)
    return () => {
      this.connectionHandlers.delete(handler)
    }
  }

  subscribeError(handler: (ev: ErrorEvent) => void): () => void {
    this.errorHandlers.add(handler)
    return () => {
      this.errorHandlers.delete(handler)
    }
  }
}
